import { DeviceMode } from '@vedalib/editor'
import type { StyleFullValue } from '@vedalib/editor/lib/brand'
import { getFontValue, getThemeValue } from '@vedalib/editor/lib/brand'
import type { StyleValue } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'

import type { FieldSelect } from 'components/formKit'
import { useFormData, useFormValues } from 'components/formKit/hooks'
import type { IFieldConfig } from 'components/formKit/types'
import type { ISelectOption } from 'components/uiKit/Select'
import { IDENTITY } from 'constants/commonConstans'
import { COMPANY_PATHS } from 'constants/paths'
import { useFontsGroupAll } from 'gql/fontsGroup/apollo'
import { usePathParams } from 'routes/hooks'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import {
  createFontFamilyOption,
  createFontWeightOptions,
  FONT_FAMILY_OPTIONS,
} from 'utils/font/hooks'

import { genColorOptions } from '../constants/constants'
import { useBrandTheme } from '../hooks'

export const useStyleFullValueSetter = ({ name }: IFieldConfig) => {
  const rawValue = useFormValues<StyleFullValue>(name)
  return useMemo(
    () => ({ setter: (value: StyleValue) => ({ ...rawValue, value }) }),
    [rawValue],
  ) as Partial<IFieldConfig>
}

export const useBrandColorOptions = () => {
  const theme = useBrandTheme()
  const options = useMemo(() => genColorOptions(theme), [theme])

  return useMemo(() => ({ props: { labeled: true, options } }), [options])
}

export const useFontFamilyOptions = <D,>({ name, getter = IDENTITY }: IFieldConfig<D>) => {
  const { companyId } = usePathParams(COMPANY_PATHS.main)
  const { data } = useFontsGroupAll({ companyId })
  const fontFamily = useFormData(name)

  const fontId = useMemo(
    () => (getFontValue(String(getter(fontFamily))) || null).id,
    [getter, fontFamily],
  )

  return useMemo(() => {
    return {
      props: {
        options: [
          {
            label: t('input.option.user'),
            value: 'custom',
            options: (data?.data || [])
              .filter(({ isArchived, id }) => id === fontId || !isArchived)
              .map(createFontFamilyOption),
          },
          {
            label: t('input.option.general'),
            value: 'default',
            options: FONT_FAMILY_OPTIONS,
          },
        ] satisfies ISelectOption<string>[],
      },
    }
  }, [data?.data, fontId]) satisfies Partial<
    IFieldConfig<D, React.ComponentProps<typeof FieldSelect<string>>>
  >
}

export const useFontWeightOptions = <D,>({ name, getter = IDENTITY }: IFieldConfig<D>) => {
  const { companyId } = usePathParams(COMPANY_PATHS.main)
  const { data } = useFontsGroupAll({ companyId })
  const values = useFormData<{ fontStyle: unknown; fontFamily: unknown }>(name, 1)
  const fontFamily = getter(values?.fontFamily)
  const fontStyle = getter(values?.fontStyle)

  return useMemo(() => {
    const font = fontFamily ? getFontValue(String(fontFamily)) : null
    const fontGroup = data?.data.find(({ id }) => id === font?.id)

    return {
      props: { options: createFontWeightOptions(font, fontGroup, String(fontStyle)) },
    }
  }, [data?.data, fontFamily, fontStyle]) satisfies Partial<
    IFieldConfig<D, React.ComponentProps<typeof FieldSelect<number>>>
  >
}

export const useShowBorder = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const data = useFormData<{ borderStyle?: string }>(name, 1)
  const border = getter(data?.borderStyle)

  return useMemo(() => ({ hidden: border === 'none' }), [border])
}

export const useShowImage = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const data = useFormData<{ background?: string }>(name, 1)
  const bg = getter(data?.background)

  return useMemo(() => ({ hidden: !bg }), [bg])
}

export const useThemeValueGetter = <D, P>(config: IFieldConfig) => {
  const theme = useBrandTheme()
  const getter = config.getter || IDENTITY
  return useMemo(
    () => ({
      getter: (value) => {
        const v = getter(value)
        return typeof v === 'string' ? getThemeValue(v, theme) : v
      },
    }),
    [getter, theme],
  ) as Partial<IFieldConfig<D, P>>
}

export const useShowDesktop = (config: IFieldConfig) => {
  const hidden = !config.name?.split('.').includes(DeviceMode.desktop)
  return useMemo(() => ({ hidden }), [hidden])
}

export const useHiddenTestFeedback = ({ name }: IFieldConfig<Block>) => {
  const data = useFormData<{ displayFeedback?: boolean }>(name, 1)
  const hidden = data?.displayFeedback === false
  return useMemo(() => ({ hidden }), [hidden])
}
